import React from 'react';
import { Link } from 'react-router-dom';
import { FiPhone, FiMail, FiMapPin, FiClock, FiHome, FiInfo } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import settings from '../config/settings';

// WhatsApp Icon Component
const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
  </svg>
);

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = settings.quickLinks;
  const programLinks = settings.programLinks;

  return (
    <footer className="relative bg-gradient-to-br from-white via-yellow-50/50 to-amber-50/40 border-t border-slate-200/60 overflow-hidden">
      {/* Modern Background Decorations */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Main gradient orbs */}
        <div className="absolute -top-40 -left-40 w-[500px] h-[500px] bg-gradient-to-br from-yellow-50/15 via-amber-50/10 to-transparent rounded-full blur-3xl"></div>
        <div className="absolute top-1/4 -right-40 w-[450px] h-[450px] bg-gradient-to-bl from-orange-50/12 via-yellow-50/8 to-transparent rounded-full blur-3xl"></div>
        <div className="absolute -bottom-32 left-1/4 w-[400px] h-[400px] bg-gradient-to-tr from-amber-50/15 to-transparent rounded-full blur-3xl"></div>

        {/* Subtle pattern overlay */}
        <div className="absolute inset-0 opacity-[0.02]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='1.5'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          backgroundSize: '60px 60px'
        }}></div>
      </div>

      {/* Main Footer Content */}
      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-8 lg:py-12">
        {/* 4 Equal Columns Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6 mb-8">

          {/* Column 1: Brand Identity */}
          <div className="space-y-4">
            <Link to="/" className="inline-block group transition-all duration-500 hover:scale-105 hover:-translate-y-0.5">
              <img
                src={settings.logo}
                alt={`${settings.siteName} Logo`}
                className="h-12 w-auto drop-shadow-sm"
              />
            </Link>

            <p className="text-slate-500 text-[13px] font-medium">
              Experience excellence in education
            </p>
            <p className="text-slate-600 leading-[1.7] text-[15px] font-light pr-2">
              Cultivating excellence in early education through innovative, child-centered learning experiences.
            </p>

            {/* Social Media - Modern Interactive */}
            <div className="flex items-center gap-2.5 pt-1">
              {settings.socialMedia.facebook && (
                <a
                  href={settings.socialMedia.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-xl bg-slate-50 border border-slate-200/60 text-slate-400 hover:text-white hover:bg-blue-600 hover:border-blue-600 hover:-translate-y-1 hover:shadow-lg hover:shadow-blue-500/20 transition-all duration-300 flex items-center justify-center group"
                  aria-label="Visit our Facebook page"
                >
                  <svg className="w-[18px] h-[18px] transition-transform duration-300 group-hover:scale-110" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                </a>
              )}
              {settings.socialMedia.instagram && (
                <a
                  href={settings.socialMedia.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-xl bg-slate-50 border border-slate-200/60 text-slate-400 hover:text-white hover:bg-gradient-to-br hover:from-purple-600 hover:to-pink-600 hover:border-transparent hover:-translate-y-1 hover:shadow-lg hover:shadow-pink-500/20 transition-all duration-300 flex items-center justify-center group"
                  aria-label="Visit our Instagram page"
                >
                  <svg className="w-[18px] h-[18px] transition-transform duration-300 group-hover:scale-110" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12.017 0C8.396 0 7.996.014 6.79.067 5.584.12 4.775.302 4.082.566c-.727.28-1.342.653-1.956 1.267C1.512 2.446 1.14 3.06.86 3.788.596 4.48.414 5.29.361 6.495.308 7.7.294 8.1.294 11.72s.014 4.02.067 5.226c.053 1.205.235 2.014.499 2.707.264.692.637 1.307 1.251 1.921.614.614 1.23.987 1.921 1.251.693.264 1.502.446 2.707.499C7.996 21.986 8.396 22 12.017 22s4.02-.014 5.226-.067c1.205-.053 2.014-.235 2.707-.499.692-.264 1.307-.637 1.921-1.251.614-.614.987-1.23 1.251-1.921.264-.693.446-1.502.499-2.707.053-1.206.067-1.606.067-5.226s-.014-4.02-.067-5.226c-.053-1.205-.235-2.014-.499-2.707-.264-.692-.637-1.307-1.251-1.921C20.554 1.512 19.94 1.14 19.212.86c-.692-.296-1.502-.478-2.707-.531C16.02.294 15.62.28 12 .28zm0 1.71c3.57 0 3.99.014 5.396.078 1.305.06 2.011.271 2.491.451.508.191.931.442 1.345.856.414.414.665.837.856 1.345.18.48.391 1.186.451 2.491.064 1.406.078 1.826.078 5.396s-.014 3.99-.078 5.396c-.06 1.305-.271 2.011-.451 2.491-.191.508-.442.931-.856 1.345-.414.414-.837.665-1.345.856-.48.18-1.186.391-2.491.451-1.406.064-1.826.078-5.396.078s-3.99-.014-5.396-.078c-1.305-.06-2.011-.271-2.491-.451-.508-.191-.931-.442-1.345-.856-.414-.414-.665-.837-.856-1.345-.18-.48-.391-1.186-.451-2.491C1.71 15.99 1.696 15.57 1.696 12s.014-3.99.078-5.396c.06-1.305.271-2.011.451-2.491.191-.508.442-.931.856-1.345.414-.414.837-.665 1.345-.856.48-.18 1.186-.391 2.491-.451C8.027 1.994 8.447 1.98 12.017 1.98zm0 3.418c-3.67 0-6.642 2.972-6.642 6.642s2.972 6.642 6.642 6.642 6.642-2.972 6.642-6.642-2.972-6.642-6.642-6.642zm0 10.884c-2.403 0-4.352-1.949-4.352-4.352s1.949-4.352 4.352-4.352 4.352 1.949 4.352 4.352-1.949 4.352-4.352 4.352zm8.474-11.09c0 .86-.696 1.556-1.556 1.556s-1.556-.696-1.556-1.556.696-1.556 1.556-1.556 1.556.696 1.556 1.556z" />
                  </svg>
                </a>
              )}
              {settings.socialMedia.twitter && (
                <a
                  href={settings.socialMedia.twitter}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-xl bg-slate-50 border border-slate-200/60 text-slate-400 hover:text-white hover:bg-sky-500 hover:border-sky-500 hover:-translate-y-1 hover:shadow-lg hover:shadow-sky-500/20 transition-all duration-300 flex items-center justify-center group"
                  aria-label="Visit our Twitter page"
                >
                  <svg className="w-[18px] h-[18px] transition-transform duration-300 group-hover:scale-110" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
                  </svg>
                </a>
              )}
              {settings.socialMedia.linkedin && (
                <a
                  href={settings.socialMedia.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-xl bg-slate-50 border border-slate-200/60 text-slate-400 hover:text-white hover:bg-blue-700 hover:border-blue-700 hover:-translate-y-1 hover:shadow-lg hover:shadow-blue-700/20 transition-all duration-300 flex items-center justify-center group"
                  aria-label="Visit our LinkedIn page"
                >
                  <svg className="w-[18px] h-[18px] transition-transform duration-300 group-hover:scale-110" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                </a>
              )}
              {settings.socialMedia.youtube && (
                <a
                  href={settings.socialMedia.youtube}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-xl bg-slate-50 border border-slate-200/60 text-slate-400 hover:text-white hover:bg-red-600 hover:border-red-600 hover:-translate-y-1 hover:shadow-lg hover:shadow-red-500/20 transition-all duration-300 flex items-center justify-center group"
                  aria-label="Visit our YouTube channel"
                >
                  <svg className="w-[18px] h-[18px] transition-transform duration-300 group-hover:scale-110" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                  </svg>
                </a>
              )}
            </div>
          </div>

          {/* Column 2: Quick Links */}
          <div>
            <h3 className="text-slate-900 font-bold text-[11px] uppercase tracking-[0.2em] mb-7 relative inline-block">
              <span className="relative z-10">Quick Links</span>
              <div className="absolute bottom-0 left-0 w-12 h-0.5 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full"></div>
            </h3>
            <ul className="space-y-1.5">
              {quickLinks.map((link, index) => (
                <li key={link.name} className="transform transition-all duration-300 hover:translate-x-2">
                  <Link
                    to={link.path}
                    className="text-slate-600 hover:text-indigo-600 transition-all duration-300 text-[15px] inline-flex items-center gap-2 font-normal group relative"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-300 group-hover:bg-indigo-500 group-hover:w-2 transition-all duration-300"></span>
                    <span className="relative">
                      {link.name}
                      <span className="absolute bottom-0 left-0 w-0 h-px bg-indigo-500 group-hover:w-full transition-all duration-300"></span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3: Programs */}
          <div>
            <h3 className="text-slate-900 font-bold text-[11px] uppercase tracking-[0.2em] mb-7 relative inline-block">
              <span className="relative z-10">Programs</span>
              <div className="absolute bottom-0 left-0 w-12 h-0.5 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full"></div>
            </h3>
            <ul className="space-y-2">
              {programLinks.map((link, index) => (
                <li key={link.name} className="transform transition-all duration-300 hover:translate-x-2">
                  <Link
                    to={link.path}
                    className="text-slate-600 hover:text-purple-600 transition-all duration-300 text-[15px] inline-flex items-center gap-2 font-normal group relative"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-300 group-hover:bg-purple-500 group-hover:w-2 transition-all duration-300"></span>
                    <span className="relative">
                      {link.name}
                      <span className="absolute bottom-0 left-0 w-0 h-px bg-purple-500 group-hover:w-full transition-all duration-300"></span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4: Contact Information */}
          <div>
            <h3 className="text-slate-900 font-bold text-[11px] uppercase tracking-[0.2em] mb-7 relative inline-block">
              <span className="relative z-10">Contact</span>
              <div className="absolute bottom-0 left-0 w-12 h-0.5 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full"></div>
            </h3>
            <div className="space-y-0">
              <div className="flex items-start gap-3 group hover:bg-slate-50/50 -ml-3 pl-3 pr-2 py-2 rounded-lg transition-all duration-300">
                <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-100 group-hover:scale-110 transition-all duration-300">
                  <SafeIcon icon={FiMapPin} className="w-4 h-4 text-blue-600" />
                </div>
                <p className="text-slate-600 text-[14px] leading-relaxed font-normal">
                  {settings.address.full}
                </p>
              </div>

              <div className="flex items-start gap-3 group hover:bg-slate-50/50 -ml-3 pl-3 pr-2 py-2 rounded-lg transition-all duration-300">
                <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0 group-hover:bg-green-100 group-hover:scale-110 transition-all duration-300">
                  <SafeIcon icon={FiPhone} className="w-4 h-4 text-green-600" />
                </div>
                <div className="text-[14px] space-y-1">
                  {settings.phones.map((phone, index) => (
                    <a
                      key={index}
                      href={`tel:${phone.number}`}
                      className="text-slate-600 hover:text-green-600 block transition-colors duration-300 font-medium"
                    >
                      {phone.display}
                    </a>
                  ))}
                </div>
              </div>

              <div className="flex items-start gap-3 group hover:bg-slate-50/50 -ml-3 pl-3 pr-2 py-2 rounded-lg transition-all duration-300">
                <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0 group-hover:bg-purple-100 group-hover:scale-110 transition-all duration-300">
                  <SafeIcon icon={FiMail} className="w-4 h-4 text-purple-600" />
                </div>
                <a
                  href={`mailto:${settings.email}`}
                  className="text-slate-600 hover:text-purple-600 text-[14px] break-all transition-colors duration-300 font-medium"
                >
                  {settings.email}
                </a>
              </div>

              <div className="flex items-start gap-3 pt-2 mt-2 border-t border-slate-100 group hover:bg-slate-50/50 -ml-3 pl-3 pr-2 py-2 rounded-lg transition-all duration-300">
                <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0 group-hover:bg-amber-100 group-hover:scale-110 transition-all duration-300">
                  <SafeIcon icon={FiClock} className="w-4 h-4 text-amber-600" />
                </div>
                <div className="text-[14px]">
                  <p className="text-slate-700 font-semibold">{settings.hours.weekdays}</p>
                  <p className="text-slate-500 text-[12px] mt-0.5">{settings.hours.note}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Bottom Bar */}
        <div className="pt-4 mt-2 border-t border-slate-200/80">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-600 text-[13px] font-medium text-center md:text-left">
              © {currentYear} <span className="text-slate-900 font-semibold">{settings.fullName}</span>. All rights reserved.
            </p>

            {/* Legal Links */}
            <div className="flex items-center gap-4">
              <Link
                to="/terms"
                className="text-slate-600 hover:text-emerald-600 text-[13px] font-medium transition-colors duration-300 relative group"
              >
                Terms & Conditions
                <span className="absolute bottom-0 left-0 w-0 h-px bg-emerald-600 group-hover:w-full transition-all duration-300"></span>
              </Link>
              <span className="text-slate-300">|</span>
              <Link
                to="/privacy"
                className="text-slate-600 hover:text-blue-600 text-[13px] font-medium transition-colors duration-300 relative group"
              >
                Privacy Policy
                <span className="absolute bottom-0 left-0 w-0 h-px bg-blue-600 group-hover:w-full transition-all duration-300"></span>
              </Link>
            </div>


          </div>
        </div>
      </div>

      {/* Mobile Bottom Navigation - Professional UI/UX */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-lg border-t border-slate-200/80 shadow-lg">
        <div className="flex items-center justify-around py-2 px-4">
          <Link
            to="/"
            className="flex flex-col items-center justify-center py-2 px-3 rounded-xl transition-all duration-300 hover:bg-slate-50 active:bg-slate-100 group min-w-0 flex-1"
          >
            <SafeIcon icon={FiHome} className="w-5 h-5 text-slate-600 group-hover:text-blue-600 transition-colors duration-300" />
            <span className="text-[10px] font-medium text-slate-600 group-hover:text-blue-600 mt-1 transition-colors duration-300">Home</span>
          </Link>

          <Link
            to="/about"
            className="flex flex-col items-center justify-center py-2 px-3 rounded-xl transition-all duration-300 hover:bg-slate-50 active:bg-slate-100 group min-w-0 flex-1"
          >
            <SafeIcon icon={FiInfo} className="w-5 h-5 text-slate-600 group-hover:text-purple-600 transition-colors duration-300" />
            <span className="text-[10px] font-medium text-slate-600 group-hover:text-purple-600 mt-1 transition-colors duration-300">About</span>
          </Link>

          <a
            href={`tel:${settings.phones[0].number}`}
            className="flex flex-col items-center justify-center py-2 px-3 rounded-xl transition-all duration-300 hover:bg-slate-50 active:bg-slate-100 group min-w-0 flex-1"
          >
            <SafeIcon icon={FiPhone} className="w-5 h-5 text-slate-600 group-hover:text-green-600 transition-colors duration-300" />
            <span className="text-[10px] font-medium text-slate-600 group-hover:text-green-600 mt-1 transition-colors duration-300">Phone</span>
          </a>

          <a
            href={settings.whatsapp.link}
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-col items-center justify-center py-2 px-3 rounded-xl transition-all duration-300 hover:bg-slate-50 active:bg-slate-100 group min-w-0 flex-1"
          >
            <WhatsAppIcon className="w-5 h-5 text-slate-600 group-hover:text-emerald-600 transition-colors duration-300" />
            <span className="text-[10px] font-medium text-slate-600 group-hover:text-emerald-600 mt-1 transition-colors duration-300">WhatsApp</span>
          </a>
        </div>
      </div>

      {/* Add bottom padding on mobile to account for fixed navigation */}
      <div className="h-16 md:hidden"></div>
    </footer>
  );
};

export default Footer;